import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, ArrowRight, Check, X, Star, ChevronRight, ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Seo, { buildServiceSchema } from "@/components/Seo";
import BeforeAfter from "@/components/BeforeAfter";
import ServiceGallerySlider from "@/components/ServiceGallerySlider";
import CTAPanel from "@/components/CTAPanel";
import { getService, SERVICES } from "@/data/services";
import { SITE } from "@/data/site";

export default function ServicePage() {
  const { slug } = useParams();
  const service = getService(slug);

  if (!service) return <Navigate to="/" replace />;

  const Icon = service.icon;
  const others = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 3);

  return (
    <>
      <Seo
        title={service.metaTitle}
        description={service.tagline}
        url={`/services/${service.slug}`}
        image={service.heroImage}
        type="service"
        schema={buildServiceSchema({ service, url: `/services/${service.slug}`, image: service.heroImage })}
      />
      {/* Breadcrumb + Hero */}
      <section data-testid="service-hero" className="relative overflow-hidden bg-brand-creamSoft">
        <div className="container-x pt-8">
          <nav className="flex items-center gap-2 text-xs text-brand-muted" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-brand-orange">Home</Link>
            <ChevronRight size={13} />
            <span className="text-brand-muted/70">Services</span>
            <ChevronRight size={13} />
            <span className="font-semibold text-brand-dark">{service.grid}</span>
          </nav>
        </div>

        <div className="container-x grid lg:grid-cols-2 gap-12 lg:gap-16 items-center py-12 md:py-16">
          <div>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-cream px-4 py-2 shadow-sm">
              <span className="grid place-items-center h-6 w-6 rounded-lg bg-brand-orange/10 text-brand-orange"><Icon size={15} /></span>
              <span className="text-xs font-semibold text-brand-dark">{service.keyword}</span>
            </motion.div>

            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.08 }} className="h1 mt-6">
              {service.h1}
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.16 }} className="lead mt-6 max-w-xl">
              {service.tagline}
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.24 }} className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/contact" data-testid="service-hero-quote-btn" className="btn-cta !py-4 text-base">
                Get Your Free Quote <ArrowRight size={18} />
              </Link>
              <a href={SITE.phoneHref} data-testid="service-hero-call-btn" className="btn-ghost-dark !py-4 text-base">
                <Phone size={17} /> {SITE.phoneDisplay}
              </a>
            </motion.div>

            <p className="mt-6 flex items-center gap-2 text-sm text-brand-muted">
              <ShieldCheck size={16} className="text-brand-orange" /> Licensed & insured · Serving {SITE.county} & all of NJ
            </p>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="overflow-hidden rounded-[28px] shadow-2xl aspect-[4/3] lg:aspect-[5/4]">
            <img src={service.heroImage} alt={service.h1} className="h-full w-full object-cover" />
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="container-x py-16 md:py-24">
        <Reveal className="max-w-3xl">
          <p className="eyebrow">The Ardeca Approach</p>
          <p className="mt-5 text-xl md:text-2xl leading-relaxed text-brand-dark font-medium">{service.intro}</p>
        </Reveal>
      </section>

      {/* Problem / Solution */}
      <section data-testid="problem-solution" className="bg-brand-creamSoft border-y border-brand-cream">
        <div className="container-x py-20 md:py-28">
          <Reveal className="max-w-2xl mb-12">
            <p className="eyebrow">Problem → Solution</p>
            <h2 className="h2 mt-4">We Fix What's Actually Bugging You</h2>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            <Reveal>
              <div className="rounded-2xl bg-white border border-brand-cream p-8 h-full shadow-sm">
                <h3 className="text-lg font-bold text-brand-dark">The Frustration</h3>
                <ul className="mt-6 space-y-4">
                  {service.problems.map((p) => (
                    <li key={p} className="flex items-start gap-3">
                      <span className="mt-0.5 grid place-items-center h-6 w-6 shrink-0 rounded-full bg-red-50 text-red-400"><X size={14} strokeWidth={3} /></span>
                      <span className="text-brand-muted leading-relaxed">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="rounded-2xl bg-brand-dark text-white p-8 h-full shadow-md">
                <h3 className="text-lg font-bold text-brand-cream">The Ardeca Fix</h3>
                <ul className="mt-6 space-y-4">
                  {service.solutions.map((p) => (
                    <li key={p} className="flex items-start gap-3">
                      <span className="mt-0.5 grid place-items-center h-6 w-6 shrink-0 rounded-full bg-brand-orange text-white"><Check size={14} strokeWidth={3} /></span>
                      <span className="text-white/85 leading-relaxed">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Features */}
      <section data-testid="service-features" className="container-x py-20 md:py-28">
        <Reveal className="max-w-2xl mb-12">
          <p className="eyebrow">What's Included</p>
          <h2 className="h2 mt-4">Craftsmanship in Every Detail</h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {service.features.map((f, i) => (
            <Reveal key={f.title} delay={(i % 4) * 0.07}>
              <div className="rounded-2xl bg-white border border-brand-cream p-6 h-full shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="grid place-items-center h-11 w-11 rounded-xl bg-brand-orange/10 text-brand-orange"><Check size={20} strokeWidth={2.4} /></div>
                <h3 className="mt-5 text-base font-bold text-brand-dark">{f.title}</h3>
                <p className="mt-2 text-sm text-brand-muted leading-relaxed">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Before / After + Gallery */}
      <section data-testid="service-gallery" className="bg-brand-creamSoft border-y border-brand-cream">
        <div className="container-x py-20 md:py-28">
          <Reveal className="max-w-2xl mb-12">
            <p className="eyebrow">Real Transformations</p>
            <h2 className="h2 mt-4">Before & After</h2>
            <p className="lead mt-5">See the difference quality craftsmanship makes. Drag the slider to reveal the transformation.</p>
          </Reveal>

          <Reveal>
            <div className="max-w-3xl mx-auto">
              <BeforeAfter before={service.beforeAfter.before} after={service.beforeAfter.after} label={service.beforeAfter.label} />
            </div>
          </Reveal>

          {/* Image Gallery Slider */}
          <div className="mt-16">
            <h3 className="text-lg font-semibold text-brand-dark mb-8">Gallery</h3>
            <Reveal>
              <ServiceGallerySlider images={service.gallery} serviceName={service.grid} />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section data-testid="service-reviews" className="container-x py-20 md:py-28">
        <Reveal className="max-w-2xl mb-12">
          <p className="eyebrow">From Our Clients</p>
          <h2 className="h2 mt-4">What Neighbors Say</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {service.reviews.map((r, i) => (
            <Reveal key={r.name} delay={i * 0.1}>
              <div className="rounded-2xl bg-white border border-brand-cream p-8 h-full shadow-sm">
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: r.rating }).map((_, j) => <Star key={j} size={17} className="fill-brand-orange text-brand-orange" />)}
                </div>
                <p className="text-brand-dark leading-relaxed">"{r.text}"</p>
                <p className="mt-5 font-extrabold text-brand-dark">{r.name}</p>
                <p className="text-sm text-brand-muted">{r.town}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Other services */}
      <section className="bg-brand-creamSoft border-y border-brand-cream">
        <div className="container-x py-16 md:py-20">
          <h2 className="h3 font-bold mb-7">Explore Our Other Services</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {others.map((s) => {
              const OIcon = s.icon;
              return (
                <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`related-service-${s.slug}`}
                  className="flex items-center gap-3 rounded-2xl bg-white border border-brand-cream p-5 hover:border-brand-orange hover:-translate-y-1 transition-all duration-300 group">
                  <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand-cream text-brand-orange group-hover:bg-brand-orange group-hover:text-white transition-colors"><OIcon size={19} /></span>
                  <span className="font-semibold text-brand-dark">{s.grid}</span>
                  <ChevronRight size={16} className="ml-auto text-brand-muted group-hover:text-brand-orange transition-colors" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <CTAPanel
        title={`Ready to Start Your ${service.grid} Project?`}
        subtitle="Get a free, no-pressure quote from a family that's been perfecting this craft for over 26 years."
      />
    </>
  );
}
